import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { isZitadelMode } from '@/app/urlParams';
import { AuthGateScreen } from '@/components/shared/AuthGateScreen';
import { preloadAllAssets } from '@/utils/assetManifest';
import { buildGameThemeStyle } from '@/utils/gameThemeStyle';
import { appConfig } from '@app/config/appConfig';
import { useAuthGate } from '@hooks/useAuthGate';

import { useGameReady } from './GameReadyContext';
import { QueryProvider } from './QueryProvider';
import { ThemeProvider } from './ThemeProvider';
import { ToastProvider } from './ToastProvider';

interface AppBootstrapGateProps {
  children: ReactNode;
}

/**
 * Preloads all offline-required assets — runs regardless of auth mode
 * (isZitadelMode skips the dev-session gate entirely, but real deployed
 * sessions still need assets warmed before gameplay can survive offline).
 */
function AssetPreloadGate({ children }: AppBootstrapGateProps) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const gameThemeStyle = useMemo(() => buildGameThemeStyle(appConfig.socket.stageNumber), []);

  useEffect(() => {
    let cancelled = false;
    preloadAllAssets()
      .then(() => {
        if (!cancelled) setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load game assets');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status !== 'ready') {
    return <AuthGateScreen gameThemeStyle={gameThemeStyle} error={error} phase="assets" />;
  }

  return <>{children}</>;
}

/**
 * Signals GameReadyContext once mounted — placed at the point where every
 * gate (asset preload + auth, if applicable) has already resolved.
 */
function GameReadySignal({ children }: AppBootstrapGateProps) {
  const { setIsGameReady } = useGameReady();
  useEffect(() => {
    setIsGameReady(true);
  }, [setIsGameReady]);
  return <>{children}</>;
}

/**
 * Inner component that runs the dev-session auth gate.
 * Only rendered in non-Zitadel mode.
 */
function DevSessionAuthGate({ children }: AppBootstrapGateProps) {
  const { isReady, error, phase } = useAuthGate();
  const gameThemeStyle = useMemo(() => buildGameThemeStyle(appConfig.socket.stageNumber), []);

  if (!isReady || error) {
    return <AuthGateScreen gameThemeStyle={gameThemeStyle} error={error} phase={phase} />;
  }

  return <>{children}</>;
}

/**
 * Outer bootstrap gate — always provides QueryProvider / ThemeProvider / ToastProvider.
 * In Zitadel mode the dev-session auth check is skipped entirely, but asset
 * preloading always runs regardless of auth mode.
 */
export function AppBootstrapGate({ children }: AppBootstrapGateProps) {
  return (
    <QueryProvider>
      <ThemeProvider>
        <ToastProvider>
          <AssetPreloadGate>
            {isZitadelMode ? (
              // Zitadel mode: token already injected in main.tsx — skip auth gate
              <GameReadySignal>{children}</GameReadySignal>
            ) : (
              <DevSessionAuthGate>
                <GameReadySignal>{children}</GameReadySignal>
              </DevSessionAuthGate>
            )}
          </AssetPreloadGate>
        </ToastProvider>
      </ThemeProvider>
    </QueryProvider>
  );
}
